/**
 * Legal Document Layout
 *
 * Standalone public shell for /privacy and /terms. Deliberately NOT the docs
 * three-column layout: these pages are read top-to-bottom, and they are fetched
 * by compliance reviewers (TCR/carrier vetting for A2P 10DLC, and payment
 * processors) who need the content to render without navigating anything.
 *
 * Server-rendered prose is not available here — this is a Vite SPA — so the
 * routes are registered eagerly rather than lazily. A reviewer's fetch still
 * needs JS, but the content must never sit behind an extra chunk load, an auth
 * check, or a redirect.
 */

import { Outlet, Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import MDXProvider from '../../components/docs/MDXProvider';
import Logo from '../../components/brand/Logo';

export default function LegalLayout() {
  const location = useLocation();

  // Deep links into a long document should start at the top, not wherever the
  // previous page was scrolled to.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <a
        href="#legal-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rack"
      >
        Skip to main content
      </a>

      <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" aria-label="ProgramRow home" className="inline-flex">
              <Logo size={14} on="light" wordmarkSize={19} />
            </Link>
            <nav className="flex items-center gap-6" aria-label="Legal">
              <Link
                to="/privacy"
                className={`text-sm font-medium transition-colors ${
                  location.pathname === '/privacy'
                    ? 'text-primary-600'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                className={`text-sm font-medium transition-colors ${
                  location.pathname === '/terms'
                    ? 'text-primary-600'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                Terms
              </Link>
              <Link
                to="/docs"
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                Docs
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <main id="legal-content" className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="prose prose-gray max-w-none prose-headings:scroll-mt-20 prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline prose-table:text-sm">
          <MDXProvider>
            <Outlet />
          </MDXProvider>
        </article>
      </main>

      <footer className="border-t border-gray-200 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <span>&copy; {new Date().getFullYear()} ProgramRow</span>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-gray-900 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-gray-900 transition-colors">
              Terms of Service
            </Link>
            <Link to="/login" className="hover:text-gray-900 transition-colors">
              Sign In
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
